import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Trophy } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import TournamentHubForm from '../../components/tournament-hub/TournamentHubForm';
import { emptyTournament } from '../../components/tournament-hub/tournamentHubUtils';
import { API } from '../../utils/apiConfig';

export default function TournamentHubSubmitPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(emptyTournament());
  const [saving, setSaving] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);

  const submit = async (force = false) => {
    if (!formData.name.trim() || !formData.tournament_date) {
      toast.error('Tournament Name and Date are required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...formData, prize_pool: formData.prize_pool ? parseFloat(formData.prize_pool) : 0 };
      const res = await axios.post(`${API}/tournament-hub`, payload, { params: force ? { force: true } : {} });
      toast.success('Tournament listed successfully!');
      setDuplicateInfo(null);
      navigate(`/hub/${res.data.id}`);
    } catch (err) {
      if (err.response?.status === 409) {
        setDuplicateInfo(err.response.data.detail);
      } else {
        toast.error(err.response?.data?.detail || 'Failed to submit tournament');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50 to-orange-50 pb-16" data-testid="tournament-hub-submit-page">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/hub')} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4" data-testid="back-to-hub-link">
          <ArrowLeft className="w-4 h-4" /> Back to Tournaments Hub
        </button>
        <div className="flex items-center gap-2 mb-1">
          <Trophy className="w-6 h-6 text-orange-500" />
          <h1 className="text-2xl font-bold text-slate-900">List Your Tournament</h1>
        </div>
        <p className="text-slate-500 mb-6 text-sm">Fill in the details below. Upload a banner to auto-fill fields with AI.</p>

        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <TournamentHubForm formData={formData} setFormData={setFormData} />
          <div className="mt-6 flex justify-end">
            <Button onClick={() => submit(false)} disabled={saving} className="bg-orange-500 hover:bg-orange-600" data-testid="submit-tournament-btn">
              {saving ? 'Submitting...' : 'Submit Tournament'}
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={!!duplicateInfo} onOpenChange={() => setDuplicateInfo(null)}>
        <DialogContent data-testid="duplicate-tournament-dialog">
          <DialogHeader><DialogTitle>Similar Tournament Found</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">{duplicateInfo?.message} Do you want to submit it anyway?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicateInfo(null)}>Cancel</Button>
            <Button className="bg-orange-500 hover:bg-orange-600" onClick={() => submit(true)} data-testid="confirm-duplicate-submit-btn">Submit Anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
